import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { TEMPLATES, BUSINESS_CATEGORIES } from "@/lib/templates";
import { SITE_LIST } from "@/lib/exampleSiteData";
import {
  Wrench, Zap, Leaf, Thermometer, Home, Paintbrush, Sparkles, Square, Box,
  Grid3x3, Shield, Waves, PanelTop, TreePine, Hammer, Droplets, Sun,
  Coffee, UtensilsCrossed, Cake, ChefHat, Truck, Beer, Scissors, Eye,
  Palette, Flower2, Syringe, Heart, Dumbbell, Activity, Swords, Music,
  Flower, ShoppingBag, Gift, Store, Recycle, Dog, Footprints, Stethoscope,
  Building2, GraduationCap, Camera, BookOpen, Calculator, FileCheck, Stamp,
  Disc3, Car, Droplet, CircleDot, AlertTriangle, Settings, Smile, Bone,
  HeartPulse, Glasses, Plus, ChevronDown, ChevronUp, ArrowRight, Smartphone,
  Search, Lock, Gauge, Phone, Check, CheckCircle2, ShieldCheck, ChevronLeft,
  ChevronRight, Menu, X
} from "lucide-react";

const ICON_MAP = {
  Wrench, Zap, Leaf, Thermometer, Home, Paintbrush, Sparkles, Square, Box,
  Grid3x3, Shield, Waves, PanelTop, TreePine, Hammer, Droplets, Sun,
  Coffee, UtensilsCrossed, Cake, ChefHat, Truck, Beer, Scissors, Eye,
  Palette, Flower2, Syringe, Heart, Dumbbell, Activity, Swords, Music,
  Flower, ShoppingBag, Gift, Store, Recycle, Dog, Footprints, Stethoscope,
  Building2, GraduationCap, Camera, BookOpen, Calculator, FileCheck, Stamp,
  Disc3, Car, Droplet, CircleDot, AlertTriangle, Settings, Smile, Bone,
  HeartPulse, Glasses, Plus
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

// ===== DESKTOP BROWSER MOCKUP =====
function BrowserMockup() {
  return (
    <div className="relative mx-auto w-full" style={{ maxWidth: 880, perspective: '1400px' }}>
      <div
        className="rounded-xl overflow-hidden"
        style={{
          transform: 'rotateX(2deg) rotateY(-2deg)',
          boxShadow: '0 50px 100px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
        }}
      >
        {/* Browser chrome */}
        <div className="flex items-center px-4 py-2.5" style={{ background: '#1C1C1E' }}>
          <div className="flex gap-1.5 mr-4">
            <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#27C93F]" />
          </div>
          <div className="flex-1 bg-[#2C2C2E] rounded-md px-3 py-1.5 flex items-center gap-1.5">
            <Lock size={10} className="text-green-500/70" />
            <span className="text-[11px] text-slate-400">sunrisecoffee.com</span>
          </div>
        </div>

        {/* Website content — scaled-down coffee shop site */}
        <div className="relative overflow-hidden bg-white" style={{ height: 420 }}>
          <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', position: 'absolute', top: 0, left: 0 }}>
            {/* Nav */}
            <div className="flex items-center justify-between px-10 py-4 bg-white border-b border-gray-100">
              <span className="text-lg font-bold" style={{ color: '#78350F', fontFamily: "'DM Serif Display', Georgia, serif" }}>Sunrise Coffee Co.</span>
              <div className="flex items-center gap-6">
                <span className="text-sm text-gray-600">Menu</span>
                <span className="text-sm text-gray-600">About</span>
                <span className="text-sm text-gray-600">Visit</span>
              </div>
              <span className="text-sm font-semibold text-white px-4 py-2 rounded-full" style={{ background: '#78350F' }}>Order Online</span>
            </div>
            {/* Hero */}
            <div className="relative flex items-center justify-center text-center" style={{ height: 780 }}>
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1600&h=900&fit=crop&q=80)' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, #78350Fdd 100%)' }} />
              <div className="relative z-10 max-w-2xl px-8">
                <h2 className="text-5xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>Sunrise Coffee Co.</h2>
                <p className="text-white/80 text-lg mb-8">Locally roasted. Locally loved. A neighborhood coffee shop in Queen Creek since 2019.</p>
                <div className="flex gap-3 justify-center">
                  <span className="px-7 py-3 rounded-full text-white font-semibold" style={{ background: '#C2410C' }}>Order Online</span>
                  <span className="px-7 py-3 rounded-full border border-white/30 text-white font-semibold">Visit Us</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== NAVBAR =====
function Navbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <nav data-testid="navbar" className="fixed top-0 w-full z-50 bg-[#0A0F1F]/70 backdrop-blur-xl border-b border-white/[0.04]">
      <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
        <a href="/" className="text-lg font-extrabold text-white tracking-tight" data-testid="navbar-logo">
          Site<span className="text-slate-500 font-medium">Smith</span>
        </a>
        <div className="hidden md:flex items-center gap-8">
          {["Examples", "Pricing", "How It Works", "About"].map(link => (
            <a key={link} href={`#${link.toLowerCase().replace(/ /g, '-')}`} className="text-[13px] text-slate-500 hover:text-white transition-colors">{link}</a>
          ))}
        </div>
        <button
          data-testid="navbar-get-started-btn"
          onClick={() => navigate('/get-started')}
          className="hidden md:block bg-white/5 border border-white/10 text-white text-sm px-5 py-2 rounded-full hover:bg-white/10 transition-colors"
        >
          Get Started
        </button>
        <button className="md:hidden text-slate-400" onClick={() => setMobileOpen(!mobileOpen)} data-testid="navbar-mobile-toggle">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#0A0F1F]/95 backdrop-blur-xl px-6 py-4 space-y-3">
          <a href="#examples" onClick={() => setMobileOpen(false)} className="block text-slate-300 text-sm">Examples</a>
          <a href="#pricing" onClick={() => setMobileOpen(false)} className="block text-slate-300 text-sm">Pricing</a>
          <a href="#how-it-works" onClick={() => setMobileOpen(false)} className="block text-slate-300 text-sm">How It Works</a>
          <button onClick={() => { setMobileOpen(false); navigate('/get-started'); }} className="w-full bg-orange-600 text-white py-2.5 rounded-full font-semibold text-sm" data-testid="navbar-mobile-cta">Get Started</button>
        </div>
      )}
    </nav>
  );
}

// ===== HERO — Clear headline + AuraMax brand element =====
function HeroSection() {
  const navigate = useNavigate();
  return (
    <section data-testid="hero-section" className="relative min-h-screen bg-[#0A0F1F] overflow-hidden flex flex-col">
      {/* Top content: headline and CTAs */}
      <div className="relative z-20 max-w-7xl mx-auto px-8 pt-28 sm:pt-32">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.span variants={fadeUp} className="inline-block text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-5 px-3 py-1 border border-orange-500/20 rounded-full">
            For local businesses
          </motion.span>
          <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-[4rem] font-extrabold text-white leading-[1.08] tracking-tight mb-6 max-w-3xl">
            Professional websites for local businesses.
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-400 text-base sm:text-lg max-w-xl mb-8 leading-relaxed">
            The average website costs $3,000 to $6,000 to build. That doesn't include hosting or security. We handle everything for $99/month. No hidden fees. No contracts.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
            <a href="#examples" className="bg-orange-600 hover:bg-orange-700 text-white px-7 py-3.5 rounded-full font-semibold transition-colors btn-shimmer" style={{ boxShadow: '0 10px 30px -5px rgba(234,88,12,0.4)' }} data-testid="hero-see-examples-btn">
              See Live Examples
            </a>
            <button onClick={() => navigate('/get-started')} className="border border-white/15 text-white/80 hover:bg-white/5 px-7 py-3.5 rounded-full font-semibold transition-colors" data-testid="hero-get-started-btn">
              Get Your Website
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Ghosted brand text + browser mockup */}
      <div className="relative z-10 flex-1 flex items-end justify-center mt-12 pb-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <span className="text-[14vw] font-extrabold text-white/[0.02] tracking-[0.12em] whitespace-nowrap">SITESMITH</span>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
          className="w-full max-w-4xl mx-auto px-4 sm:px-8"
        >
          <BrowserMockup />
        </motion.div>
      </div>

      {/* Bottom info row */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="relative z-10 max-w-7xl mx-auto px-8 pb-10 w-full grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <p className="text-slate-600 text-sm">Two brothers from Arizona. Real websites for real businesses.</p>
        <p className="text-white text-sm font-medium text-center hidden md:block">Built. Hosted. <span className="text-orange-500">Done.</span></p>
        <p className="text-slate-600 text-[11px] font-medium tracking-[0.15em] uppercase text-right hidden md:block">$99/month &bull; Everything included</p>
      </motion.div>
    </section>
  );
}

// ===== MARQUEE STRIP =====
function MarqueeStrip() {
  const items = TEMPLATES.slice(0, 22);
  return (
    <div className="overflow-hidden border-y border-white/[0.04] bg-[#0A0F1F]" data-testid="marquee-strip">
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0A0F1F] to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0A0F1F] to-transparent z-10" />
        <div className="animate-marquee">
          {[...items, ...items].map((t, i) => {
            const IconComp = ICON_MAP[t.icon] || Wrench;
            return (
              <div key={i} className="flex items-center gap-2 px-5 py-4 shrink-0">
                <IconComp size={14} strokeWidth={1.5} className="text-slate-700" />
                <span className="text-[13px] text-slate-600 whitespace-nowrap font-medium">{t.name}</span>
                <span className="text-slate-800 ml-3">&bull;</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ===== SOCIAL PROOF BAR =====
function SocialProofBar() {
  const items = [
    { icon: <Paintbrush size={14} strokeWidth={1.5} />, text: "Professional Design" },
    { icon: <Search size={14} strokeWidth={1.5} />, text: "Shows Up on Google" },
    { icon: <Smartphone size={14} strokeWidth={1.5} />, text: "Mobile Perfect" },
    { icon: <Gauge size={14} strokeWidth={1.5} />, text: "Loads Instantly" },
    { icon: <ShieldCheck size={14} strokeWidth={1.5} />, text: "Secure HTTPS" },
  ];
  return (
    <section data-testid="social-proof-bar" className="border-b border-white/[0.04] bg-[#0d1225] py-5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`proof-${i}`}>
              <CheckCircle2 size={13} className="text-emerald-500" strokeWidth={2} />
              <span className="text-[13px] text-slate-500 font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== HOW IT WORKS =====
function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Tell us about your business", desc: "Pick your business type, add your name and phone number. No design decisions, no tech skills, no headaches.", icon: <FileCheck size={26} strokeWidth={1.5} /> },
    { num: "02", title: "We hand-craft your website", desc: "Austin & Jack build a professional, mobile-first website tailored to your business. Custom colors, your services, your contact info.", icon: <Paintbrush size={26} strokeWidth={1.5} /> },
    { num: "03", title: "You get more customers", desc: "Your site goes live with built-in Google optimization. New customers find you when they search. You start getting calls.", icon: <Phone size={26} strokeWidth={1.5} /> },
  ];
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-28 bg-[#0A0F1F]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">How it works</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-16 tracking-tight">
            Three steps. We do the heavy lifting.
          </motion.h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((step) => (
            <motion.div key={step.num} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="bg-[#111827] border border-white/[0.06] p-8 rounded-sm hover:-translate-y-1 transition-transform duration-300"
              data-testid={`step-${step.num}`}
            >
              <span className="text-5xl font-extrabold text-white/[0.05] font-mono">{step.num}</span>
              <div className="text-orange-500 mt-4 mb-3">{step.icon}</div>
              <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== EXAMPLE WEBSITES — PORTFOLIO CARD GALLERY =====
function ExampleShowcase() {
  return (
    <section id="examples" data-testid="examples-section" className="py-28 bg-[#0d1225]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">Examples</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">Real websites built for real businesses.</motion.h2>
          <motion.p variants={fadeUp} className="text-slate-500 mb-12 max-w-xl text-sm">Click any example to view the full live website. Every site is fully designed for that business type.</motion.p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {SITE_LIST.map((site, i) => (
            <motion.div
              key={site.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              data-testid={`example-card-${site.slug}`}
            >
              <Link
                to={`/examples/${site.slug}`}
                className="block rounded-lg overflow-hidden border border-white/[0.06] bg-[#111827] hover:-translate-y-1 hover:border-white/15 transition-all group"
              >
                <div className="h-44 bg-cover bg-center relative" style={{ backgroundImage: `url(${site.heroImage})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider" style={{ background: site.colors.primary }}>
                      {site.slug.includes("coffee") ? "Coffee Shop" : site.slug.includes("lash") ? "Beauty" : site.slug.includes("plumb") ? "Plumbing" : site.slug.includes("bloom") ? "Florist" : site.slug.includes("desert") ? "Landscaping" : site.slug.includes("fetch") ? "Pet Grooming" : "Bakery"}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-white font-bold text-sm mb-1 group-hover:text-orange-400 transition-colors">{site.name}</h3>
                  <p className="text-slate-500 text-xs mb-3 line-clamp-2">{site.heroSubtitle}</p>
                  <span className="text-orange-500 text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Live Site <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <p className="text-slate-600 text-sm mt-10 text-center max-w-2xl mx-auto">
          These are just 8 examples. We design custom sites for every kind of local business &mdash; restaurants, salons, auto shops, medical offices, photography, real estate, fitness studios, and more.
        </p>
        <div className="text-center mt-6">
          <Link to="/get-started" className="text-orange-500 hover:text-orange-400 text-sm font-semibold inline-flex items-center gap-1" data-testid="examples-cta">
            Ready to get yours? Tell us about your business <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ===== BUSINESS TYPE GRID =====
function BusinessTypeGrid() {
  const navigate = useNavigate();
  return (
    <section id="business-types" data-testid="business-type-section" className="py-28 bg-[#0A0F1F]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">Your business type</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-12 tracking-tight">We design for all kinds of local businesses.</motion.h2>
        </motion.div>
        {BUSINESS_CATEGORIES.map((cat) => {
          const types = TEMPLATES.filter(t => t.category === cat);
          return (
            <div key={cat} className="mb-8">
              <h3 className="text-[11px] font-semibold text-slate-600 uppercase tracking-[0.12em] mb-3">{cat}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {types.map((tmpl) => {
                  const IconComp = ICON_MAP[tmpl.icon] || Wrench;
                  return (
                    <button key={tmpl.id} data-testid={`btype-${tmpl.id}`} onClick={() => navigate('/get-started')}
                      className="flex items-center gap-2.5 p-3 bg-[#111827] border border-white/[0.06] rounded-sm hover:border-white/15 hover:-translate-y-0.5 transition-all text-left group"
                    >
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${tmpl.primaryColor}18` }}>
                        <IconComp size={14} style={{ color: tmpl.primaryColor }} strokeWidth={1.75} />
                      </div>
                      <span className="text-sm text-slate-400 font-medium truncate group-hover:text-white transition-colors">{tmpl.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <button data-testid="btype-other" onClick={() => navigate('/get-started')}
          className="mt-2 flex items-center gap-2.5 p-3 bg-[#111827] border border-dashed border-white/10 rounded-sm hover:border-orange-500/40 transition-colors"
        >
          <Plus size={16} className="text-orange-500" strokeWidth={1.75} />
          <span className="text-sm text-slate-500 font-medium">Other -- Don't see your type? We design for any local business.</span>
        </button>
      </div>
    </section>
  );
}

// ===== WHAT'S INCLUDED =====
function WhatsIncludedSection() {
  const features = [
    { icon: <Smartphone size={20} strokeWidth={1.5} />, title: "Looks perfect on any device", desc: "Designed mobile-first so customers on phones, tablets, and desktops all see a beautiful site." },
    { icon: <Search size={20} strokeWidth={1.5} />, title: "Built to show up on Google", desc: "Optimized so when people search your business near me, they find you." },
    { icon: <Gauge size={20} strokeWidth={1.5} />, title: "Loads instantly", desc: "No bloated code. Your site loads in under a second, faster than 90% of small business websites." },
    { icon: <ShieldCheck size={20} strokeWidth={1.5} />, title: "Secure & always online", desc: "HTTPS security included. Hosted on enterprise-grade infrastructure that never goes down." },
    { icon: <Phone size={20} strokeWidth={1.5} />, title: "One-tap calling", desc: "Customers tap your phone number and it calls you directly. No searching, no copying." },
    { icon: <Wrench size={20} strokeWidth={1.5} />, title: "We handle everything", desc: "Hosting, security, updates, Google submission. You never touch a line of code." },
  ];
  return (
    <section data-testid="whats-included-section" className="py-28 bg-[#0d1225]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">What you get</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">
            Everything your business needs online.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-500 text-sm mb-12">$99/month covers all of it. No hidden fees.</motion.p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-[#0A0F1F] border border-white/[0.06] p-6 rounded-sm hover:border-white/10 transition-colors group" data-testid={`feature-${i}`}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(234,88,12,0.1)' }}>
                <span className="text-orange-500">{f.icon}</span>
              </div>
              <h3 className="text-white font-semibold mb-1.5">{f.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ===== COMPETITOR COMPARISON =====
function ComparisonSection() {
  const navigate = useNavigate();
  const rows = [
    { label: "Custom website", ss: "Included", others: "$3,000 - $6,000", highlight: false },
    { label: "Web hosting", ss: "Included", others: "$20 - $150/mo extra", highlight: false },
    { label: "Security (SSL)", ss: "Included", others: "$50 - $200/yr extra", highlight: false },
    { label: "DDoS protection", ss: "Included", others: "$200+/yr extra", highlight: false },
    { label: "Your monthly cost", ss: "$99/mo", others: "$70 - $350/mo (after paying $3k-$6k upfront)", highlight: true },
    { label: "First year total", ss: "$1,188", others: "$4,000 - $10,000+", highlight: true },
    { label: "Time to launch", ss: "Under a week", others: "4 - 12 weeks", highlight: false },
    { label: "Mobile optimized", ss: true, others: "Depends", highlight: false },
    { label: "Ongoing support", ss: "Austin & Jack directly", others: "Extra cost or none", highlight: false },
    { label: "Hidden fees", ss: false, others: "Usually", highlight: false, invertBool: true },
    { label: "Contract required", ss: false, others: "Often 6-12 months", highlight: false, invertBool: true },
  ];

  const BoolCell = ({ value, invert }) => {
    if (typeof value === "boolean") {
      const isGood = invert ? !value : value;
      return isGood
        ? <CheckCircle2 size={16} className="text-emerald-500 mx-auto" strokeWidth={2} />
        : <X size={16} className="text-slate-600 mx-auto" strokeWidth={2} />;
    }
    return <span>{value}</span>;
  };

  const cols = ["SiteSmith", "The Traditional Route"];
  const colKeys = ["ss", "others"];

  return (
    <section id="compare" data-testid="comparison-section" className="py-28 bg-[#0A0F1F]">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">Compare</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">SiteSmith vs. the traditional route.</motion.h2>
          <motion.p variants={fadeUp} className="text-slate-500 text-sm mb-12">Most businesses pay $3,000 to $6,000 just to get a website built. Then hosting, security, and DDoS protection are all extra. We include everything.</motion.p>
        </motion.div>

        {/* Desktop table */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="hidden lg:block overflow-hidden rounded-lg border border-white/[0.06]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left p-4 text-slate-500 font-medium w-[200px]"></th>
                {cols.map((col, i) => (
                  <th key={col} className={`p-4 text-center font-semibold ${i === 0 ? 'text-orange-400 bg-orange-500/[0.06] border-t-2 border-orange-500' : 'text-slate-400 bg-[#111827]'}`}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={`border-b border-white/[0.04] ${row.highlight ? 'bg-orange-500/[0.03]' : ''}`}>
                  <td className={`p-4 text-slate-400 font-medium ${row.highlight ? 'text-white font-semibold' : ''}`}>{row.label}</td>
                  {colKeys.map((key, j) => (
                    <td key={key} className={`p-4 text-center ${j === 0 ? 'bg-orange-500/[0.04] font-semibold text-white' : 'text-slate-500'} ${row.highlight && j === 0 ? 'text-orange-400 font-bold text-lg' : ''}`}>
                      <BoolCell value={row[key]} invert={row.invertBool} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Mobile cards */}
        <div className="lg:hidden space-y-4">
          {cols.map((col, ci) => (
            <motion.div key={col} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className={`rounded-lg border p-5 ${ci === 0 ? 'border-orange-500/30 bg-orange-500/[0.04]' : 'border-white/[0.06] bg-[#111827]'}`}
            >
              <h3 className={`font-bold mb-4 ${ci === 0 ? 'text-orange-400' : 'text-slate-400'}`}>{col}</h3>
              <div className="space-y-2.5">
                {rows.map((row, ri) => (
                  <div key={ri} className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs">{row.label}</span>
                    <span className={`text-sm font-medium ${ci === 0 ? 'text-white' : 'text-slate-400'} ${row.highlight && ci === 0 ? 'text-orange-400 font-bold' : ''}`}>
                      <BoolCell value={row[colKeys[ci]]} invert={row.invertBool} />
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-slate-500 text-sm text-center mt-8">
          Stop overpaying. Get the same quality website for $99/month with everything included.
        </motion.p>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-5">
          <button onClick={() => navigate('/get-started')} className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-full font-semibold transition-colors btn-shimmer" style={{ boxShadow: '0 8px 24px -5px rgba(234,88,12,0.3)' }} data-testid="comparison-cta">
            Get Started
          </button>
        </motion.div>
      </div>
    </section>
  );
}

// ===== PRICING =====
function PricingSection() {
  const navigate = useNavigate();
  return (
    <section id="pricing" data-testid="pricing-section" className="py-28 bg-[#0A0F1F]">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">Pricing</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-10 tracking-tight">One plan. Everything included. No surprises.</motion.h2>
          <motion.div variants={fadeUp} className="relative border border-orange-500/20 rounded-sm p-8 sm:p-12 bg-[#111827]" style={{ boxShadow: '0 0 80px rgba(234,88,12,0.06)' }}>
            <p className="text-sm font-semibold text-orange-500 mb-6 tracking-wide">SiteSmith Website</p>
            <div className="flex items-baseline justify-center gap-2 mb-2">
              <span className="text-5xl sm:text-6xl font-extrabold text-white">$99</span>
              <span className="text-slate-500 text-sm">/month</span>
            </div>
            <p className="text-slate-600 text-sm mb-8">That's it. No setup fees. No hidden costs. Nothing else to buy.</p>
            <div className="space-y-2.5 max-w-sm mx-auto text-left mb-8">
              {[
                "Fully custom website, built for your business",
                "Top-of-the-line web hosting",
                "Security protection (SSL certificate)",
                "DDoS protection",
                "Mobile-optimized for every device",
                "Google search optimization",
                "Unlimited updates to your info",
                "Real human support from Austin & Jack",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 size={15} className="text-emerald-500 shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-slate-400 text-sm">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-sm mb-2">No contracts. Cancel anytime.</p>
            <p className="text-white/90 text-sm font-semibold mb-6">Zero hidden fees. We mean it.</p>
            <button data-testid="pricing-cta-button" onClick={() => navigate('/get-started')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-3.5 text-base font-semibold rounded-full transition-colors btn-shimmer"
              style={{ boxShadow: '0 10px 30px -5px rgba(234,88,12,0.4)' }}
            >
              Get Your Website
            </button>
          </motion.div>
          <motion.p variants={fadeUp} className="text-slate-600 text-sm mt-6">
            The average website costs $3,000 to $6,000 to build, and that doesn't include hosting or security. We do it all for $99/month.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// ===== ABOUT =====
function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="py-28 bg-[#0d1225]">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">Who we are</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-6 tracking-tight">Built by two brothers who care about local business.</motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 leading-relaxed mb-4">
            We're Austin and Jack, two brothers from Arizona. We watched great local businesses lose customers to competitors with better websites. So we built SiteSmith to give every local business a professional online presence for $99/month, everything included. No $5,000 invoices. No surprises.
          </motion.p>
          <motion.p variants={fadeUp} className="text-slate-400 leading-relaxed">
            When you sign up, you're not buying from a faceless corporation. You're working with two real people who answer their own emails.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// ===== FAQ =====
function FAQSection() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: "What does $99/month include?", a: "Everything. A fully custom website built for your business, top-of-the-line web hosting, security protection, and DDoS protection. There is nothing else to buy." },
    { q: "Are there any hidden fees?", a: "None. Zero. $99/month is all you pay. No setup fees. No add-ons. No surprise charges. No 'oh by the way' invoices. We include everything because that's how it should be." },
    { q: "How much does a website normally cost?", a: "The average website costs $3,000 to $6,000 just to build. And that does not include hosting, security, or DDoS protection. Those are extra ongoing costs on top of the upfront price tag. We handle the website, the hosting, the security, all for $99/month." },
    { q: "Do I need any tech skills?", a: "None. If you can type your business name and phone number, you're good. We handle all the design and tech." },
    { q: "Can I use my own domain name?", a: "Yes. You get a free SiteSmith subdomain right away. If you want a custom domain like yourbusiness.com, grab one from any registrar (about $12/year) and we'll connect it at no extra charge." },
    { q: "Why is this so much cheaper than hiring a web designer?", a: "A typical web designer charges $3,000 to $6,000 because they're building your site from scratch. We built smart systems that let us create beautiful, professional websites efficiently without cutting corners on quality. Lower overhead means we can charge $99/month for everything instead of sending you a $5,000 invoice." },
    { q: "Can I cancel anytime?", a: "Yes. No contracts, no cancellation fees, no phone calls required. Cancel whenever you want. But we think you'll stick around." },
  ];
  return (
    <section id="faq" data-testid="faq-section" className="py-28 bg-[#0A0F1F]">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.15em] text-orange-500 mb-3">Questions</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-12 tracking-tight">Common questions, straight answers.</motion.h2>
        </motion.div>
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="border border-white/[0.06] rounded-sm overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors" data-testid={`faq-toggle-${i}`}>
                <span className="text-white font-medium pr-4">{faq.q}</span>
                {open === i ? <ChevronUp size={16} className="text-slate-600 shrink-0" /> : <ChevronDown size={16} className="text-slate-600 shrink-0" />}
              </button>
              {open === i && <div className="px-5 pb-5 text-slate-500 text-sm leading-relaxed">{faq.a}</div>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== FINAL CTA =====
function FinalCTASection() {
  const navigate = useNavigate();
  return (
    <section data-testid="final-cta-section" className="py-28 bg-[#0d1225]">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">Your next customer is searching for you right now.</motion.h2>
          <motion.p variants={fadeUp} className="text-slate-500 text-lg mb-8">Be the business they find.</motion.p>
          <motion.div variants={fadeUp}>
            <button data-testid="final-cta-button" onClick={() => navigate('/get-started')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-4 text-lg font-semibold rounded-full transition-colors btn-shimmer"
              style={{ boxShadow: '0 10px 30px -5px rgba(234,88,12,0.4)' }}
            >
              Get Started
            </button>
            <p className="text-slate-700 text-sm mt-4">Fill out a 60-second form. We'll send you a preview within 24 hours.</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ===== FOOTER =====
function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#060a14] border-t border-white/[0.04] py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <span className="text-lg font-extrabold text-white">Site<span className="text-slate-600 font-medium">Smith</span></span>
          <div className="flex items-center gap-6 text-[13px] text-slate-600">
            <a href="#how-it-works" className="hover:text-slate-400 transition-colors">How It Works</a>
            <a href="#examples" className="hover:text-slate-400 transition-colors">Examples</a>
            <a href="#pricing" className="hover:text-slate-400 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-slate-400 transition-colors">FAQ</a>
            <a href="/get-started" className="hover:text-slate-400 transition-colors">Contact</a>
          </div>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-800">
          <span>Based in Arizona</span>
          <span>&copy; 2026 SiteSmith. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-slate-600">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-600">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ===== LANDING PAGE =====
export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#0A0F1F]">
      <Navbar />
      <HeroSection />
      <MarqueeStrip />
      <SocialProofBar />
      <HowItWorksSection />
      <ExampleShowcase />
      <BusinessTypeGrid />
      <WhatsIncludedSection />
      <ComparisonSection />
      <PricingSection />
      <AboutSection />
      <FAQSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}
